'use client';
import React, { useState, useRef, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, RotateCcw, Headphones } from 'lucide-react';

interface AudioPlayerProps {
  title: string;
  duration: string;
  audioUrl: string | null;
}

export default function AudioPlayer({ title, duration, audioUrl }: AudioPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Backend integration point: audioUrl from ElevenLabs generation job
  // When audioUrl is null, player shows "generating" state

  function togglePlay() {
    if (!audioUrl) return;
    if (playing) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
    setPlaying((v) => !v);
  }

  function handleProgressClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!audioRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * audioRef.current.duration;
    setProgress(pct * 100);
  }

  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current) return;
    const pct = (audioRef.current.currentTime / audioRef.current.duration) * 100;
    setProgress(isNaN(pct) ? 0 : pct);
  }, []);

  function restart() {
    if (!audioRef.current) return;
    audioRef.current.currentTime = 0;
    setProgress(0);
    setPlaying(false);
  }

  const formatTime = (pct: number, totalSecs: number) => {
    const secs = Math.floor((pct / 100) * totalSecs);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const totalSecs = 9 * 60;

  return (
    <div className="audio-player">
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onEnded={() => setPlaying(false)}
          muted={muted}
        />
      )}

      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Headphones size={14} className="text-white/70" />
            <p className="text-xs font-semibold text-white/70 uppercase tracking-widest">
              Your meditation
            </p>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">{title}</h2>
          <p className="text-sm text-white/60 mt-0.5">{duration} · Personalised for you</p>
        </div>
        {!audioUrl && (
          <span className="text-xs font-medium text-white/60 bg-white/10 rounded-full px-3 py-1.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 bg-amber-300 rounded-full animate-pulse" />
            Generating audio…
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div
        className="progress-bar-track mb-3 cursor-pointer"
        onClick={handleProgressClick}
      >
        <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-white/50 tabular-nums">
          {formatTime(progress, totalSecs)}
        </span>
        <div className="flex items-center gap-3">
          <button
            onClick={restart}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            title="Restart"
          >
            <RotateCcw size={14} className="text-white" />
          </button>
          <button
            onClick={togglePlay}
            disabled={!audioUrl}
            className="w-12 h-12 rounded-full bg-white flex items-center justify-center hover:scale-105 active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
            title={!audioUrl ? 'Audio generating…' : playing ? 'Pause' : 'Play'}
          >
            {playing ? (
              <Pause size={20} className="text-[#2A9D8F]" />
            ) : (
              <Play size={20} className="text-[#2A9D8F] ml-0.5" />
            )}
          </button>
          <button
            onClick={() => setMuted((v) => !v)}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            title={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? (
              <VolumeX size={14} className="text-white" />
            ) : (
              <Volume2 size={14} className="text-white" />
            )}
          </button>
        </div>
        <span className="text-xs text-white/50 tabular-nums">{duration}</span>
      </div>

      {!audioUrl && (
        <p className="text-center text-xs text-white/50 mt-3 italic">
          Your audio is being generated. Refresh in a few minutes.
        </p>
      )}
    </div>
  );
}